/* ===== Roles & Permissions — canonical catalog shared by web and mobile ===== */

export type RoleName =
  | 'organization_admin'
  | 'manager'
  | 'treasurer'
  | 'financial_reviewer'
  | 'moderator'
  | 'member'
  | 'viewer'

export const ROLES = {
  ORG_ADMIN: 'organization_admin',
  MANAGER: 'manager',
  TREASURER: 'treasurer',
  FINANCIAL_REVIEWER: 'financial_reviewer',
  MODERATOR: 'moderator',
  MEMBER: 'member',
  VIEWER: 'viewer',
} as const satisfies Record<string, RoleName>

export const ROLE_LABELS: Record<RoleName, string> = {
  organization_admin: 'Administrador de organizacion',
  manager: 'Encargado de proyectos',
  treasurer: 'Tesorero',
  financial_reviewer: 'Revisor financiero',
  moderator: 'Moderador',
  member: 'Miembro',
  viewer: 'Invitado o solo lectura',
}

export const PERMISSIONS = {
  ORG_MANAGE: 'organization.manage',
  MEMBERS_VIEW: 'members.view',
  MEMBERS_INVITE: 'members.invite',
  MEMBERS_MANAGE: 'members.manage',
  ANNOUNCEMENTS_CREATE: 'announcements.create',
  ANNOUNCEMENTS_EDIT: 'announcements.edit',
  ANNOUNCEMENTS_DELETE: 'announcements.delete',
  PROJECTS_CREATE: 'projects.create',
  PROJECTS_MANAGE: 'projects.manage',
  EVENTS_CREATE: 'events.create',
  EVENTS_MANAGE: 'events.manage',
  DOCUMENTS_UPLOAD: 'documents.upload',
  DOCUMENTS_DELETE: 'documents.delete',
  CHANNELS_CREATE: 'channels.create',
  CHANNELS_MODERATE: 'channels.moderate',
  CAMPAIGNS_CREATE: 'campaigns.create',
  CAMPAIGNS_MANAGE: 'campaigns.manage',
  PAYMENTS_VIEW: 'payments.view',
  REPORTS_VIEW: 'reports.view',
  AUDIT_VIEW: 'audit.view',
  FINANCE_CATEGORIES_MANAGE: 'finance.categories.manage',
  FINANCE_INCOME_CREATE: 'finance.income.create',
  FINANCE_INCOME_EDIT: 'finance.income.edit',
  FINANCE_EXPENSE_CREATE: 'finance.expense.create',
  FINANCE_EXPENSE_APPROVE: 'finance.expense.approve',
  FINANCE_REPORTS_VIEW: 'finance.reports.view',
  FINANCE_REPORTS_EXPORT: 'finance.reports.export',
  DUES_VIEW_ALL: 'dues.view_all',
  DUES_MANAGE: 'dues.manage',
  SUPPLIES_MANAGE: 'supplies.manage',
  SUPPLIES_OFFERINGS_CREATE: 'supplies.offerings.create',
  SUPPLIES_RESERVATIONS_VIEW: 'supplies.reservations.view',
  SUPPLIES_DELIVERY_MANAGE: 'supplies.delivery.manage',
} as const

const ALL_PERMISSIONS = Object.values(PERMISSIONS)

export const ROLE_PERMISSIONS: Record<RoleName, string[]> = {
  organization_admin: ALL_PERMISSIONS,
  manager: [
    PERMISSIONS.PROJECTS_CREATE,
    PERMISSIONS.PROJECTS_MANAGE,
    PERMISSIONS.EVENTS_CREATE,
    PERMISSIONS.EVENTS_MANAGE,
    PERMISSIONS.CAMPAIGNS_CREATE,
    PERMISSIONS.CAMPAIGNS_MANAGE,
    PERMISSIONS.SUPPLIES_MANAGE,
    PERMISSIONS.SUPPLIES_OFFERINGS_CREATE,
    PERMISSIONS.SUPPLIES_RESERVATIONS_VIEW,
    PERMISSIONS.SUPPLIES_DELIVERY_MANAGE,
  ],
  treasurer: [
    PERMISSIONS.FINANCE_CATEGORIES_MANAGE,
    PERMISSIONS.FINANCE_INCOME_CREATE,
    PERMISSIONS.FINANCE_INCOME_EDIT,
    PERMISSIONS.FINANCE_EXPENSE_CREATE,
    PERMISSIONS.FINANCE_REPORTS_VIEW,
    PERMISSIONS.FINANCE_REPORTS_EXPORT,
    PERMISSIONS.DUES_VIEW_ALL,
    PERMISSIONS.DUES_MANAGE,
    PERMISSIONS.PAYMENTS_VIEW,
  ],
  financial_reviewer: [PERMISSIONS.FINANCE_EXPENSE_APPROVE, PERMISSIONS.FINANCE_REPORTS_VIEW],
  moderator: [
    PERMISSIONS.ANNOUNCEMENTS_CREATE,
    PERMISSIONS.ANNOUNCEMENTS_EDIT,
    PERMISSIONS.ANNOUNCEMENTS_DELETE,
    PERMISSIONS.CHANNELS_CREATE,
    PERMISSIONS.CHANNELS_MODERATE,
    PERMISSIONS.DOCUMENTS_UPLOAD,
    PERMISSIONS.DOCUMENTS_DELETE,
  ],
  member: [],
  viewer: [],
}

export function getPermissionsForRoles(roles: readonly RoleName[]): string[] {
  const result = new Set<string>()
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role] ?? []) {
      result.add(permission)
    }
  }
  return Array.from(result)
}

export function isKnownRole(value: string): value is RoleName {
  return Object.prototype.hasOwnProperty.call(ROLE_LABELS, value)
}
